// "Customise Table" — lets a coach pick which stat columns show in the
// Table view and save the selection as a named, reusable template. Unlike
// challenge-report's CustomizeTableModal (which falls back to an "apply
// without saving" mode when the name is left blank), this one always
// requires a name and always saves — one button, matching the design.
//
// Doubles as the EDIT modal for an existing template (same design as the
// mockup — there's no separate "edit" screen): when `editingTemplate` is
// passed, the form prefills from it instead of starting blank, `onSave`
// is expected to PUT rather than POST (TableTemplateControls.js decides
// that based on the same prop), and a delete icon-button appears in the
// footer (mirrors LeaderboardTemplateCard's confirm-then-delete pattern).

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Modal from "../../../../../parts/modal-new";
import SvgIcon from "../../../../../parts/SvgIcon";
import { TABLE_STAT_GROUPS } from "./viewTokens";

const tt = (t, key, opts) => t(`nutritionPage.progress.tableTemplate.${key}`, opts);

// One column of the "Select Stats" grid — mirrors challenge-report's
// CustomizeTableModal's CheckboxGrid, simplified (single fixed column here,
// not a configurable cols count, since TABLE_STAT_GROUPS already encodes
// which keys belong in which column).
// "sessionRPE" is the only key whose LABEL follows the coach's rpe/rir
// preference (same as the table column header) — the stored key never
// changes, so a template saved under RPE still applies correctly if the
// coach later switches to RIR.
const CheckboxColumn = ({ keys, checkedSet, onToggle, t, isRir }) => (
  <ul className="np-customize-table-modal__column">
    {keys.map((key) => {
      const labelKey = key === "sessionRPE" && isRir ? "sessionRIR" : key;
      return (
        <li key={key}>
          <label className="np-customize-table-modal__checkbox-label">
            <input
              type="checkbox"
              checked={checkedSet.has(key)}
              onChange={() => onToggle(key)}
            />
            {tt(t, `stats.${labelKey}`)}
          </label>
        </li>
      );
    })}
  </ul>
);

CheckboxColumn.propTypes = {
  keys: PropTypes.arrayOf(PropTypes.string).isRequired,
  checkedSet: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  isRir: PropTypes.bool,
};

export const CustomizeNutritionTableModal = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  editingTemplate = null,
  intensityMeasure = "rpe",
}) => {
  const isRir = intensityMeasure === "rir";
  const { t } = useTranslation("athlete-portal");
  const [templateName, setTemplateName] = useState("");
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Reset every time the modal opens — blank for a new template, or
  // prefilled from editingTemplate when opened via the dropdown's edit icon.
  useEffect(() => {
    if (isOpen) {
      setTemplateName(editingTemplate?.name ?? "");
      setSelectedKeys(new Set(editingTemplate?.columns ?? []));
      setSaveError(null);
    }
  }, [isOpen, editingTemplate]);

  /**
   * Toggles the selection of a key in the selectedKeys set.
   * @param {*} key 
   */
  const toggleKey = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const canSave = templateName.trim().length > 0 && selectedKeys.size > 0;

  /**
   * Handles the saving of the template. Stays open with an inline error on
   * failure instead of closing blind — onSave (NutritionProgress.js) throws
   * on a non-ok response.
   */
  const handleSave = async () => {
    if (!canSave || isSaving) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave({ name: templateName.trim(), columns: [...selectedKeys] });
      onClose();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Deletes editingTemplate after a confirm() prompt — same
   * confirm-then-delete pattern as LeaderboardTemplateCard's handleDelete.
   * Stays open with an inline error on failure, same as handleSave.
   */
  const handleDelete = async () => {
    if (!editingTemplate || isDeleting) return;
    if (!window.confirm(tt(t, "confirmDelete"))) return;
    setIsDeleting(true);
    setSaveError(null);
    try {
      await onDelete(editingTemplate._id);
      onClose();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      title={tt(t, "modalTitle")}
      className="np-customize-table-modal"
      onClose={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div className="body np-customize-table-modal__body">
        <label htmlFor="np-table-template-name">{tt(t, "templateNamePlaceholder")}</label>
        <input
          id="np-table-template-name"
          type="text"
          className="np-customize-table-modal__name-input"
          placeholder={tt(t, "templateNamePlaceholder")}
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          required
        />

        <section className="np-customize-table-modal__section">
          <h3 className="np-customize-table-modal__section-header">{tt(t, "selectStats")}</h3>
          <div className="np-customize-table-modal__columns">
            {TABLE_STAT_GROUPS.map((keys, i) => (
              <CheckboxColumn
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                keys={keys}
                checkedSet={selectedKeys}
                onToggle={toggleKey}
                t={t}
                isRir={isRir}
              />
            ))}
          </div>
        </section>
      </div>

      {/* Own line, full width — kept OUT of the footer's flex row (and out
          of the delete icon's wrapper) so a save/update error never reads
          as if it were about the delete action next to it. */}
      {saveError && (
        <p className="np-modal-footer__error" role="alert">{saveError}</p>
      )}

      <footer className="np-customize-table-modal__footer">
        <div className="np-customize-table-modal__footer-start">
          {editingTemplate && (
            <button
              type="button"
              data-button-type="secondary"
              data-icon-only
              className="np-customize-table-modal__delete-btn"
              onClick={handleDelete}
              disabled={isDeleting}
              aria-label={tt(t, "deleteTemplate")}
            >
              <SvgIcon src="/img/new-icons/red-trash.svg" noFilterOnLight />
            </button>
          )}
        </div>
        <button
          type="button"
          data-button-type="secondary"
          className="np-modal-footer__cancel-btn"
          onClick={onClose}
        >
          {tt(t, "cancel")}
        </button>
        <button
          type="button"
          data-button-type="primary"
          className="np-modal-footer__save-btn"
          onClick={handleSave}
          disabled={!canSave || isSaving}
        >
          {tt(t, "applyAndSave")}
        </button>
      </footer>
    </Modal>
  );
};

CustomizeNutritionTableModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  editingTemplate: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string,
    columns: PropTypes.array,
  }),
  intensityMeasure: PropTypes.string,
};

export default CustomizeNutritionTableModal;
